import { useRolodexEdit } from 'features/fde/rolodexAPI'
import type { MdCase } from 'features/mdCases/mdCase'
import { MD_CASE_SEVERITIES } from 'features/mdCases/mdCase'
import { useGeckoPermissions } from 'features/permissions'
import { useCallback, useMemo } from 'react'

/**
 * Convert agent-emitted plain text / lightweight markdown into
 * minimal HTML so the case page's Lexical editor renders it
 * correctly. The agent typically emits paragraphs separated by
 * blank lines and uses **bold** for emphasis; the case editor
 * stores HTML, so saving plain text directly leaves the field
 * looking blank.
 *
 * We only convert when the value does not already look like HTML
 * (no recognized opening tag) — agents are free to emit HTML and
 * we don't want to double-encode.
 */
function ensureNotesHtml(value: string): string {
  if (!value) return value
  if (/<(p|div|ul|ol|h[1-6]|br|strong|em|u|s|span|li)\b/i.test(value)) {
    return value
  }
  const escapeHtml = (raw: string): string =>
    raw.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  const escaped = escapeHtml(value)
  // Convert markdown-style **bold** and *italic* (basic cases).
  const bolded = escaped.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
  const italicized = bolded.replace(
    /(^|[^*])\*(?!\*)([^*\n]+?)\*(?!\*)/g,
    '$1<em>$2</em>'
  )
  // Split on blank lines into paragraphs; single newlines become <br>.
  const paragraphs = italicized
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0)
  if (paragraphs.length === 0) return ''
  return paragraphs.map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('')
}

/**
 * V1 allowlisted field paths for `propose_case_update`. The backend
 * tool enforces this list authoritatively; the FE accept handler
 * uses it to switch on which case mutator to call.
 */
export type ProposableFieldPath =
  | 'meta.title'
  | 'workflow.severity'
  | 'notes.working_notes'
  | 'notes.recommendation_draft'

export type ProposalStatus = 'pending' | 'accepted' | 'rejected' | 'superseded'

export interface CaseProposal {
  id: string
  caseId: string
  fieldPath: ProposableFieldPath
  currentValue: unknown
  proposedValue: unknown
  rationale: string
  citations: Array<Record<string, unknown>>
  status: ProposalStatus
  createdAt: number
  decidedAt: number | null
  decidedBy: string | null
}

/**
 * Case-mutation functions a proposal accept handler dispatches into.
 * Sourced from `useMdCaseDetails()` on the case page; kept abstract
 * here so this module doesn't import the page provider directly.
 */
export interface CaseUpdateHandlers {
  updateTitle: (title: string) => Promise<void>
  updateWorkingNotes: (notes: string) => Promise<void>
  updateRecommendationDraft: (draft: string) => Promise<void>
  updateWorkflow: (workflow: Partial<MdCase['workflow']>) => Promise<void>
}

export function useCaseProposalAPI() {
  const { rolodexPatch } = useRolodexEdit()
  const { user: currentUser } = useGeckoPermissions()

  const acceptProposal = useCallback(
    async (
      proposal: CaseProposal,
      handlers: CaseUpdateHandlers
    ): Promise<void> => {
      // Apply the case mutation FIRST. If it fails, do not mark the
      // proposal as accepted — leave the card in `pending` so the
      // user can retry.
      switch (proposal.fieldPath) {
        case 'meta.title': {
          if (typeof proposal.proposedValue !== 'string') {
            throw new Error('meta.title proposal must be a string')
          }
          await handlers.updateTitle(proposal.proposedValue)
          break
        }
        case 'workflow.severity': {
          if (
            typeof proposal.proposedValue !== 'string' ||
            !MD_CASE_SEVERITIES.includes(
              proposal.proposedValue as (typeof MD_CASE_SEVERITIES)[number]
            )
          ) {
            throw new Error(
              `workflow.severity proposal must be one of ${MD_CASE_SEVERITIES.join(
                ', '
              )}`
            )
          }
          await handlers.updateWorkflow({
            severity:
              proposal.proposedValue as (typeof MD_CASE_SEVERITIES)[number],
          })
          break
        }
        case 'notes.working_notes': {
          if (typeof proposal.proposedValue !== 'string') {
            throw new Error('notes.working_notes proposal must be a string')
          }
          await handlers.updateWorkingNotes(
            ensureNotesHtml(proposal.proposedValue)
          )
          break
        }
        case 'notes.recommendation_draft': {
          if (typeof proposal.proposedValue !== 'string') {
            throw new Error(
              'notes.recommendation_draft proposal must be a string'
            )
          }
          await handlers.updateRecommendationDraft(
            ensureNotesHtml(proposal.proposedValue)
          )
          break
        }
        default: {
          // Exhaustiveness check
          const _never: never = proposal.fieldPath
          throw new Error(`Unsupported field path: ${String(_never)}`)
        }
      }

      // Then mark the proposal accepted. If this PATCH fails after
      // the case mutation succeeded, the card will still show
      // `pending` on reload until the next status fetch — a small
      // UX wart, not a correctness issue (the case is already
      // updated; activity log shows the user as actor).
      // Note: we update only `data.status` here. The proposal entry
      // also has a `status` tag set at create time which becomes
      // stale on accept; v1 reads status from data rather than tags
      // (tags are an index hint, not the source of truth).
      await rolodexPatch([
        {
          id: proposal.id,
          data: {
            status: 'accepted',
            decided_at: Date.now(),
            decided_by: currentUser?.email ?? null,
          },
        },
      ])
    },
    [rolodexPatch, currentUser?.email]
  )

  const rejectProposal = useCallback(
    async (proposalId: string): Promise<void> => {
      await rolodexPatch([
        {
          id: proposalId,
          data: {
            status: 'rejected',
            decided_at: Date.now(),
            decided_by: currentUser?.email ?? null,
          },
        },
      ])
    },
    [rolodexPatch, currentUser?.email]
  )

  return useMemo(
    () => ({ acceptProposal, rejectProposal }),
    [acceptProposal, rejectProposal]
  )
}

/**
 * Type-narrowed parser for a Rolodex `case-proposal` entry's data
 * payload. Use this when reading proposals via `useRolodexEntries`
 * to ensure the FE doesn't choke on schema drift.
 */
export function parseCaseProposalEntry(
  entryId: string,
  entryData: Record<string, unknown> | null | undefined
): CaseProposal | null {
  if (!entryData) return null
  const fieldPath = entryData.field_path
  if (
    fieldPath !== 'meta.title' &&
    fieldPath !== 'workflow.severity' &&
    fieldPath !== 'notes.working_notes' &&
    fieldPath !== 'notes.recommendation_draft'
  ) {
    return null
  }
  const status = entryData.status
  const validStatus: ProposalStatus =
    status === 'accepted' ||
    status === 'rejected' ||
    status === 'superseded' ||
    status === 'pending'
      ? status
      : 'pending'

  return {
    id: entryId,
    caseId: typeof entryData.case_id === 'string' ? entryData.case_id : '',
    fieldPath,
    currentValue: entryData.current_value,
    proposedValue: entryData.proposed_value,
    rationale:
      typeof entryData.rationale === 'string' ? entryData.rationale : '',
    citations: Array.isArray(entryData.citations)
      ? (entryData.citations as Array<Record<string, unknown>>)
      : [],
    status: validStatus,
    createdAt:
      typeof entryData.created_at === 'number' ? entryData.created_at : 0,
    decidedAt:
      typeof entryData.decided_at === 'number' ? entryData.decided_at : null,
    decidedBy:
      typeof entryData.decided_by === 'string' ? entryData.decided_by : null,
  }
}
